import './featured-collection.scss';

import {register, load} from '@shopify/theme-sections';

/**
 * Section: Featured Collection
 * ------------------------------------------------------------------------------
 * Large component displaying image galleries in a carousel.
 *
 * @namespace featuredCollection
 */

import Carousel from '~/components/carousel';

/**
 * DOM selectors.
 */
const selectors = {
  id: 'featuredCollection',
  pagination: '[js-featured-collection="pagination"]',
  nextButton: '[js-featured-collection="nextButton"]',
  prevButton: '[js-featured-collection="prevButton"]',
};

/**
 * Instance globals.
 */
let featuredCollection = {};

/**
 * Featured Collection section constructor.
 * Executes on page load as well as Theme Editor `section:load` events.
 */
register('featured-collection', {

  /**
   * Defaults.
   */
  getConfig() {
    return {
      swiper: {
        autoplay: this.getAutoplaySetting(),
        navigation: {
          nextEl: this.nextButton,
          prevEl: this.prevButton,
        },
        pagination: {
          el: this.pagination,
          type: 'fraction',
          clickable: true,
        },
        loop: false,
        spaceBetween: 0,
        slidesPerView: 'auto',
        slidesPerGroup: 2,
        breakpoints: {
          1024: {
            slidesPerGroup: 4,
          }
        },
        freeModeSticky: true,
        lazy: true,
      }
    };
  },

  /**
   * Cache selectors.
   * - Define necessary variables and make available for component use.
   */
  cacheSelectors() {
    const container = `#${selectors.id}-${this.id}`;
    const pagination = `${container} ${selectors.pagination}`;
    const nextButton = `${container} ${selectors.nextButton}`;
    const prevButton = `${container} ${selectors.prevButton}`;


    this.cache = {
      container,
      pagination,
      nextButton,
      prevButton,
    };
  },

  /**
   * Set cached node selectors.
   */
  setNodeSelectors() {
    this.container = document.querySelector(this.cache.container);
    this.pagination = document.querySelector(this.cache.pagination);
    this.nextButton = document.querySelector(this.cache.nextButton);
    this.prevButton = document.querySelector(this.cache.prevButton);
  },

  /**
   * Fetches carousel autoplay setting
   * - Reads data-autoplay attribute on carousel container.
   * @returns {Boolean|Object} Autoplay setting object formatted for Swiper.
   */
  getAutoplaySetting() {
    const autoplay = Number(this.container.dataset.autoplay);
    if (!autoplay) { return false; }

    return {
      delay: autoplay,
    };
  },

  /**
   * Construct carousel.
   */
  constructCarousel() {
    if (!this.container) {
      throw new Error(`Could not find container of ${this.cache.container}`);
    }

    featuredCollection = Carousel(this.cache.container, this.getConfig());
    featuredCollection.init();

    this.swiperInstance = featuredCollection.getSwiperInstance();
  },

  /**
   * Event callback when Section is registered in scripts.
   */
  onLoad() {
    this.cacheSelectors();
    this.setNodeSelectors();
    this.constructCarousel();
  },

  /**
   * Event callback for Theme Editor `section:unload` event.
   */
  onUnload() {
    featuredCollection.resize();
  },

  /**
   * Event callback for Theme Editor `section:select` event.
   */
  onSelect() {
    featuredCollection.resize();
  },

  onBlockSelect(event) {
    this.swiperInstance.autoplay.stop();

    const slide = event.target;
    const slideId = slide.dataset.swiperSlideIndex;
    featuredCollection.select(slideId, 0);
  },

  onBlockDeselect() {
    this.swiperInstance.autoplay.start();
  },
});

load('featured-collection');
